import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { PAYSLIP_APPROVED, PAYSLIP_DRAFT, PAYSLIP_LOCKED } from "../constants";
import {
  InvalidateExpensesPayload,
  ManualPayslipPayload,
  Payslip,
  PayslipDetailed,
  PayslipDetailedParams,
  PayslipEvent,
  PayslipFilters,
  PayslipTotals,
  PayslipsMoreResponse,
  PayslipsResponse,
  UpdatePayslipPayload,
  approvePayslip,
  deletePayslip,
  emptyPayslip,
  generateManualPayslip,
  generateSalaryPayment,
  getMorePayslips,
  getPayslipById,
  getPayslips,
  invalidateExpenses,
  lockPayslip,
  savePayslipChanges,
  unapprovePayslip,
} from "./payslipAPI";
import { Hits } from "../../../common/types/Paginated";

export type SliceState = {
  ids: [];
  entities: {
    [id: string]: Payslip;
  };
  cursor: string | null;
  hits?: Hits;
  totals: PayslipTotals | null;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

const payslipAdapter = createEntityAdapter<Payslip>({
});

const handlePayslipEvents = (
  payload: PayslipEvent[],
  existingPayslip?: Payslip,
) => {
  let payslip: Payslip = { ...emptyPayslip, ...existingPayslip };
  payload.forEach((event) => {
    switch (event.name) {
      case "pspNew":
        payslip = {
          ...payslip,
          id: event.id,
          version: event.version,
          isManual: event.payload.isManual,
          status: {
            code: PAYSLIP_DRAFT,
          },
          eezyCompanyId: event.payload.eezyCompanyId,
          payslipNumber: event.payload.payslipNumber,
          talentId: event.payload.talentId,
          periodStart: event.payload.periodStart,
          periodEnd: event.payload.periodEnd,
          valueDate: event.payload.valueDate,
          salaryDate: event.payload.salaryDate,
          payer: {
            name: event.payload.eezyCompanyName,
          },
          recipient: {
            name: event.payload.talentName,
          },
        };
        break;
      case "pspPmtdChg":
        payslip = {
          ...payslip,
          version: event.version,
          recipient: {
            ...payslip.recipient,
            ssn: event.payload.ssn,
          },
        };
        break;
      case "pspStlsChg":
        payslip = {
          ...payslip,
          version: event.version,
          subtotalsPeriod: {
            ...event.payload.period,
          },
        };
        break;
      case "pspApr":
        payslip = {
          ...payslip,
          version: event.version,
          status: {
            code: PAYSLIP_APPROVED,
          },
        };
        break;
      case "pspLoc":
        payslip = {
          ...payslip,
          version: event.version,
          status: {
            code: PAYSLIP_LOCKED,
          },
        };
        break;
      default:
        payslip = {
          ...payslip,
          version: event.version,
        };
        break;
    }
    return;
  });
  return payslip;
};

export const fetchPayslipById = createAsyncThunk<
  PayslipDetailed,
  PayslipDetailedParams,
  { state: RootState }
>(
  "payslips/fetchPayslipById",
  async (params: PayslipDetailedParams, { requestId }) => {
    const response = await getPayslipById(params, {
      requestId,
    });
    return response.data;
  },
);

export const fetchPayslips = createAsyncThunk<
  PayslipsResponse,
  PayslipFilters,
  { state: RootState }
>("payslips/fetchPayslips", async (filters: PayslipFilters, { requestId }) => {
  const response = await getPayslips(filters, {
    requestId,
  });
  return response;
});

export const fetchMorePayslips = createAsyncThunk<
  PayslipsMoreResponse,
  PayslipFilters,
  { state: RootState }
>(
  "payslips/fetchMorePayslips",
  async (filters: PayslipFilters, { requestId }) => {
    const response = await getMorePayslips(filters, {
      requestId,
    });
    return response.data;
  },
);

export const postApprovePayslip = createAsyncThunk<
  PayslipEvent[],
  string,
  { state: RootState }
>("talents/postApprovePayslip", async (payslipId: string, { requestId }) => {
  const res = await approvePayslip(payslipId, { requestId });
  return res.data;
});

export const postUnapprovePayslip = createAsyncThunk<
  PayslipEvent[],
  string,
  { state: RootState }
>("talents/postUnapprovePayslip", async (payslipId: string, { requestId }) => {
  const res = await unapprovePayslip(payslipId, { requestId });
  return res.data;
});

export const postLockPayslip = createAsyncThunk<
  PayslipEvent[],
  string,
  { state: RootState }
>("talents/postLockPayslip", async (payslipId: string, { requestId }) => {
  const res = await lockPayslip(payslipId, { requestId });
  return res.data;
});

export const postDeletePayslip = createAsyncThunk<
  PayslipEvent[],
  string,
  { state: RootState }
>("talents/postDeletePayslip", async (payslipId: string, { requestId }) => {
  const res = await deletePayslip(payslipId, { requestId });
  return res.data;
});

export const createManualPayslip = createAsyncThunk<
  PayslipEvent[],
  ManualPayslipPayload,
  { state: RootState }
>(
  "payslips/createManualPayslip",
  async (payload: ManualPayslipPayload, { requestId }) => {
    const response = await generateManualPayslip(payload, {
      requestId,
    });
    return response.data;
  },
);

export const updatePayslip = createAsyncThunk<
  PayslipEvent[],
  UpdatePayslipPayload,
  { state: RootState }
>(
  "payslips/updatePayslip",
  async (payload: UpdatePayslipPayload, { requestId }) => {
    const response = await savePayslipChanges(payload, {
      requestId,
    });
    return response.data;
  },
);

export const recalculateExpenses = createAsyncThunk<
  any,
  InvalidateExpensesPayload,
  { state: RootState }
>("payslips/recalculateTalentExpenses", async (payload, { requestId }) => {
  const response = await invalidateExpenses(payload, { requestId });
  return response.data;
});

export const createSalaryPayment = createAsyncThunk<
  PayslipEvent[],
  { payslipId: string },
  { state: RootState }
>("payslips/createSalaryPayment", async (payload, { requestId }) => {
  const response = await generateSalaryPayment(payload, { requestId });
  return response.data;
});

export const payslipSlice = createSlice({
  name: "payslips",
  initialState: payslipAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayslips.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchPayslips.fulfilled, (state, action) => {
        payslipAdapter.setAll(state, action.payload.data);
        state.cursor = action.payload.cursor;
        state.hits = action.payload.hits;
        state.totals = action.payload.totals;
        state.status = "succeeded";
      })
      .addCase(fetchPayslips.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchMorePayslips.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchMorePayslips.fulfilled, (state, action) => {
        payslipAdapter.upsertMany(state, action.payload.data);
        state.cursor = action.payload.cursor;
        state.status = "succeeded";
      })
      .addCase(fetchMorePayslips.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(postApprovePayslip.pending, (state) => {
        state.status = "pending";
      })
      .addCase(postApprovePayslip.fulfilled, (state, action) => {
        if (action.payload) {
          payslipAdapter.updateOne(state, {
            id: action.meta.arg,
            changes: { status: { code: PAYSLIP_APPROVED } },
          });
        }
        state.status = "succeeded";
      })
      .addCase(postApprovePayslip.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(postUnapprovePayslip.pending, (state) => {
        state.status = "pending";
      })
      .addCase(postUnapprovePayslip.fulfilled, (state, action) => {
        if (action.payload) {
          payslipAdapter.updateOne(state, {
            id: action.meta.arg,
            changes: { status: { code: PAYSLIP_DRAFT } },
          });
        }
        state.status = "succeeded";
      })
      .addCase(postUnapprovePayslip.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(postLockPayslip.pending, (state) => {
        state.status = "pending";
      })
      .addCase(postLockPayslip.fulfilled, (state, action) => {
        //@ts-ignore
        const oldPayslip = state.entities[action.meta.arg] as Payslip;
        const updatedPayslip = handlePayslipEvents(action.payload, oldPayslip);
        payslipAdapter.updateOne(state, {
          id: action.meta.arg,
          changes: updatedPayslip,
        });
        state.status = "succeeded";
      })
      .addCase(postLockPayslip.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(postDeletePayslip.pending, (state) => {
        state.status = "pending";
      })
      .addCase(postDeletePayslip.fulfilled, (state, action) => {
        payslipAdapter.removeOne(state, action.meta.arg);
        state.hits!.value -= 1;
        state.status = "succeeded";
      })
      .addCase(postDeletePayslip.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(createManualPayslip.pending, (state) => {
        state.status = "pending";
      })
      .addCase(createManualPayslip.fulfilled, (state, action) => {
        if (action.payload.length > 0) {
          const newPayslip = handlePayslipEvents(action.payload);
          payslipAdapter.addOne(state, newPayslip);
          state.hits!.value += 1;
        }
        state.status = "succeeded";
      })
      .addCase(createManualPayslip.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(updatePayslip.pending, (state) => {
        state.status = "pending";
      })
      .addCase(updatePayslip.fulfilled, (state, action) => {
        if (action.payload.length > 0) {
          const payslipId = action.meta.arg.payslipId;
          const payslip = state.entities[payslipId];

          action.payload.reduce((a: Payslip, event: PayslipEvent) => {
            switch (event.name) {
              case "pspSaldChg":
                a.version = event.version;
                a.salaryDate = event.payload.salaryDate;
                break;
              case "pspValdChg":
                a.version = event.version;
                a.valueDate = event.payload.valueDate;
                break;
              case "pspDel":
                payslipAdapter.removeOne(state, payslipId);
                break;
              default:
                a.version = event.version;
                break;
            }
            return a;
          }, payslip);
        }
        state.status = "succeeded";
      })
      .addCase(updatePayslip.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(recalculateExpenses.pending, (state) => {
        state.status = "pending";
      })
      .addCase(recalculateExpenses.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(recalculateExpenses.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      });
  },
});

export const { selectAll, selectById, selectIds } = payslipAdapter.getSelectors(
  (state: RootState) => state.payslips,
);

export default payslipSlice.reducer;
